/**
 * XION Medical product data — Integrated system solutions (EndoSTROBE / EndoOFFICE / EndoFLEX / EndoCOMPACT / EndoSURGERY / EndoPORTABLE)
 * Content sourced from xion-medical.com (split from completeProductsData.js).
 */

export const endoSTROBESystem = {
  id: 'endostrobe-spectar-system',
  name: 'EndoSTROBE Spectar',
  slug: 'endostrobe-spectar',
  category: 'Laryngo-stroboscopy and phoniatrics',
  tagline: 'The ultimate workstation for phonosurgery',
  highlight: 'Top-of-the-line, best-in-class Endoscopy Tower for ENT',
  description: 'Based on the EndoSTROBE PL camera processor, the comprehensively equipped workstation meets all the requirements of voice diagnostics and surgical applications.',
  fullDescription: 'The EndoSTROBE Spectar system represents the ultimate workstation for phonosurgery, combining advanced video-stroboscopic technology with comprehensive voice diagnostics capabilities. Together with the DiVAS software, the MATRIX DS data station forms the basis for digitally acquiring, managing and evaluating patient and examination data and for connecting to hospital information systems or medical office software via standard interfaces such as DICOM / HL7 or GDT.',
  features: [
    'One universal system for rigid & flexible ENT video endoscopy — office, ambulance and OR',
    'Native sensor resolutions up to 4K (4× HD) plus 2D & 3D on a single platform',
    'Noiseless, flicker-free stroboscopy in 4K, HD and 3D — including world-first 3D stroboscopy',
    'PIET Professional Image Enhancement Technology: lumino, chromo and spectro modes',
    'Real-time EGG (electroglottography) superimposed on the live stroboscopic image',
    'Patented light-intensity control prevents burns; picture-in-picture function',
    '3D endoscopy and 3D microscopy on one platform with rapid 3D/2D switching',
    'Easy integration of existing surgical microscopes (ZEISS, LEICA) via C-mount / 3D microscope camera',
    'XION PowerControl, OR-compatible wireless footswitch and laser-optical galvanic isolation for patient safety'
  ],
  piet: [
    { mode: 'PIET lumino', desc: 'Both bright and dark areas are equally well represented.' },
    { mode: 'PIET chromo', desc: 'Details are highlighted and the colour contrast is intensified.' },
    { mode: 'PIET spectro', desc: 'By shifting the colour spectrum, tissue structures are displayed in a more differentiated manner.' }
  ],
  divasModules: [
    'Base / Patient Management',
    'Video Documentation (Full HD/4K, 3D)',
    'Photo Documentation',
    'Stroboscopy Analysis',
    'Strobokymography',
    'Electroglottography (EGG)',
    'Swallowing Diagnostics (FEES)',
    'Singing- & Speaking-Voice Analysis',
    'Voice Range Profile (DSI)',
    'Voice Strain Test',
    'Voice Handicap Index (VHI)'
  ],
  components: [
    { name: 'EndoSTROBE PL Spectar Camera Processor', link: '/products/camera-processors-for-laryngo-stroboscopy/endostrobe-pl-spectar' },
    { name: 'Spectar Camera Head HD with Microphone', link: '/products/camera-heads/spectar-camera-head-hd-microphone' },
    { name: 'Spectar Camera Head 4K', link: '/products/camera-heads/spectar-camera-head-4k-uhd' },
    { name: 'Spectar Video Nasopharyngoscope (XN HD)', link: '/products/flexible-video-endoscopes/spectar-video-nasopharyngoscopes' },
    { name: 'MATRIX DS Data Station (Tower PC)', link: '/products/documentation-recording-systems/matrix-ds-data-station' },
    { name: 'DiVAS 2.8 Software', link: '/products/software/divas-2-8' },
    { name: 'MATRIX LED duo Light Source', link: '/products/led-illumination-systems/matrix-led-duo' },
    { name: 'Medical Grade Monitors', link: '/products/medical-grade-monitors/medical-monitor-27-4k' }
  ],
  applications: [
    'Rigid & flexible ENT video endoscopy (office · ambulance · OR)',
    'Video microscopy',
    'Stroboscopy (2D, HD, 4K and 3D)',
    'Swallowing diagnostics (FEES)',
    'Electroglottography (EGG)',
    'Voice diagnostics'
  ],
  images: [
    '/products/transparent/endostrobe-spectar-system-web.png',
    '/products/transparent/endostrobe-pl-spectar-web.png',
    '/products/transparent/spectar-video-nasopharyngoskop-sterrad-web.png',
    '/products/transparent/spectar-kamerakopf-hd-mit-mikrofon-web.png',
    '/products/transparent/divas-2-8-web.png'
  ]
};

export const endoOFFICESpectarSystem = {
  id: 'endooffice-spectar-system',
  name: 'EndoOFFICE Spectar',
  slug: 'endooffice-spectar',
  category: 'Office Endoscopy',
  tagline: 'A compact system for diagnostics and outpatient surgery',
  description: 'The EndoOFFICE Spectar system configuration based on the MATRIX EL processor, a Panel PC and the DiVAS software is suitable for integration in treatment units in outpatient clinics or medical offices for acquiring, managing and evaluating examination data and for connecting the system to the HIS or PACS via standard interfaces such as DICOM / HL7 or to medical office software systems via standard interfaces such as GDT.',
  fullDescription: 'Featuring an integrated high-performance LED light source and excellent image quality through specialised image processing routines, the EndoOFFICE Spectar offers universal connectivity for camera heads with sensor resolutions up to FullHD. Camera heads with zoom lens 16-32 mm or fixed focal length 22 mm and individually programmable buttons complete the system. The processor can be attached to a roller stand with the EndoDESK back wall system or integrated directly into treatment units.',
  features: [
    'Integrated high-performance LED light source',
    'Excellent image quality by means of image processing routines specially adapted to the application',
    'Universal connectivity for camera heads with sensor resolutions up to FullHD',
    'Camera heads with zoom lens 16-32 mm or fixed focal length 22 mm and individually programmable buttons',
    'Compact design suitable for office integration',
    'Can be attached to a roller stand (EndoDESK back wall system) or integrated into treatment units',
    'DiVAS software for patient data management',
    'HIS/PACS connectivity via DICOM/HL7 and GDT'
  ],
  components: [
    { name: 'MATRIX EL Spectar Camera Processor', link: '/products/camera-processors-for-office-and-mobile-applications/matrix-el-spectar' },
    { name: 'Spectar Camera Head HD Zoom', link: '/products/camera-heads/spectar-camera-head-hd-zoom' },
    { name: 'Panel PC 21.5"', link: '/products/documentation-recording-systems/panel-pc-documentation' },
    { name: 'DiVAS 2.8 Software', link: '/products/software/divas-2-8' },
    { name: 'Spectar Video Nasopharyngoscopes', link: '/products/flexible-video-endoscopes/spectar-video-nasopharyngoscopes' },
    { name: 'Rigid Endoscopes', link: '/products/rigid-endoscopes/sinuscopes' }
  ],
  applications: [
    'Office-based endoscopy',
    'Outpatient diagnostics',
    'Minor surgical procedures',
    'Treatment unit integration'
  ],
  images: [
    '/products/transparent/endooffice-spectar-web.png',
    '/products/transparent/matrix-el-spectar-web.png',
    '/products/transparent/panelpc-web.png',
    '/products/transparent/spectar-kamerakopf-hd-mit-mikrofon-web.png',
    '/products/transparent/divas-2-8-web.png'
  ]
};

export const endoOFFICEVeezarSystem = {
  id: 'endooffice-veezar-system',
  name: 'EndoOFFICE Veezar',
  slug: 'endooffice-veezar',
  category: 'Office Endoscopy',
  tagline: 'Compact and powerful office endoscopy',
  description: 'The EndoOFFICE Veezar combines powerful technology with sophisticated design for everyday medical use. The 21.5" Panel PC with touchscreen, in combination with the Veezar CF10 compact camera, the IH20 illumination head and DiVAS 2.8, forms a highly integrated endoscopy system with excellent image quality.',
  fullDescription: 'Thanks to its compact system design, EndoOFFICE Veezar fits seamlessly into any working environment – either as a tabletop version or mobile on a rolling stand. Developed for the highest demands in medical visualisation, the system is built around the Veezar imaging platform and the DiVAS 2.8 software for recording, processing and managing patient and examination data.',
  features: [
    'Veezar Compact Camera CF10 with Full HD resolution',
    'Combines with all standard rigid endoscopes and laryngoscopes',
    'Veezar Illumination Head IH20 with powerful, long-life LED',
    'Homogeneous illumination of rigid and flexible endoscopes',
    '21.5" medical Panel PC with touchscreen',
    'DiVAS 2.8 software for documentation and data management',
    'Compact design — tabletop or mobile on a rolling stand',
    'Seamless integration into any working environment'
  ],
  components: [
    { name: 'Veezar Compact Camera CF10', link: '/products/compact-cameras/veezar-compact-camera-cf10' },
    { name: 'Veezar Illumination Head IH20', link: '/products/led-illumination-systems/veezar-illumination-head-ih20' },
    { name: 'Panel PC 21.5"', link: '/products/documentation-recording-systems/panel-pc-documentation' },
    { name: 'DiVAS 2.8 Software', link: '/products/software/divas-2-8' },
    { name: 'Rigid Endoscopes & Laryngoscopes', link: '/products/rigid-endoscopes/laryngoscopes' }
  ],
  applications: [
    'Office-based endoscopy',
    'ENT examinations',
    'Outpatient diagnostics',
    'Laryngoscopy'
  ],
  images: [
    '/products/transparent/endooffice-veezar-web.png',
    '/products/transparent/veezar-kompaktkamera-web.png',
    '/products/transparent/veezar-beleuchtungskopf-ih20-web.png',
    '/products/transparent/panelpc-web.png',
    '/products/transparent/divas-2-8-web.png'
  ]
};

export const endoFLEXSpectarSystem = {
  id: 'endoflex-spectar-system',
  name: 'EndoFLEX Spectar',
  slug: 'endoflex-spectar',
  category: 'Laryngo-Stroboscopy & Swallowing Diagnostics',
  tagline: 'Compact and powerful flexible video endoscopy',
  description: 'The EndoFLEX Spectar system configuration comprises a MATRIX E Spectar camera processor, Panel PC and DiVAS software for flexible video-endoscopy at the highest level.',
  fullDescription: 'Flexible video-endoscopy at the highest level. The EndoFLEX Spectar system provides comprehensive capabilities for acquiring, managing and evaluating examination data and connecting to HIS or PACS via DICOM/HL7 or GDT interfaces. Examinations are performed with Spectar series video nasopharyngoscopes.',
  features: [
    'MATRIX E Spectar camera processor',
    'Panel PC with DiVAS software',
    'Spectar series video nasopharyngoscopes (XN HD, XN S, XN P)',
    'DICOM/HL7/GDT connectivity',
    'EndoDESK back panel system',
    'Integrated endoscope holder',
    'Drawer for small utensils',
    'Modular and extensible design'
  ],
  components: [
    { name: 'MATRIX E Spectar Camera Processor', link: '/products/camera-processors-for-office-and-mobile-applications/matrix-e-spectar' },
    { name: 'Spectar Video Nasopharyngoscope XN HD', link: '/products/flexible-video-endoscopes/spectar-video-nasopharyngoscopes' },
    { name: 'Panel PC 21.5"', link: '/products/documentation-recording-systems/panel-pc-documentation' },
    { name: 'DiVAS 2.8 Software', link: '/products/software/divas-2-8' },
    { name: 'EndoDESK Back Panel System', link: null },
    { name: 'Roller Stand', link: null }
  ],
  applications: [
    'Flexible video endoscopy',
    'Swallowing diagnostics',
    'Laryngoscopy',
    'ENT examinations',
    'Office-based procedures'
  ],
  images: [
    '/products/transparent/endoflex-spectar-web.png',
    '/products/transparent/matrix-e-spectar-web.png',
    '/products/transparent/spectar-video-nasopharyngoskop-sterrad-web.png',
    '/products/transparent/panelpc-web.png',
    '/products/transparent/divas-2-8-web.png'
  ]
};

export const endoCOMPACTVeezarSystem = {
  id: 'endocompact-veezar-system',
  name: 'EndoCOMPACT Veezar',
  slug: 'endocompact-veezar',
  category: 'Laryngo-Stroboscopy & Phoniatrics',
  tagline: 'The compact endoscopy system for ENT',
  description: 'EndoCOMPACT Veezar consists of an interactive console with integrated fixtures for holding the most important imaging instruments used in ENT medicine and phoniatrics.',
  fullDescription: 'XION Veezar stands for a new, powerful imaging platform in endoscopy. In combination with a Panel PC and the DiVAS software platform, the user is provided with a complete workstation for all endoscopic applications. Different needs can be covered with different equipment for voice diagnostic examinations, stroboscopy, otoscopy or bedside swallowing diagnostics.',
  features: [
    'Interactive console with integrated instrument holders',
    'Automatic examination mode detection when instrument is taken',
    'Ergonomic design for intuitive working',
    'Mobile or fixed installation options',
    'Integration with Panel PC and DiVAS software',
    'Veezar Video Nasopharyngoscopes FN10 and FN20',
    'Veezar Video Otoscope VO10',
    'Veezar Compact Camera CF20',
    'Veezar Illumination Head IH10 with integrated microphone'
  ],
  components: [
    { name: 'Veezar Video Nasopharyngoscope FN10', link: '/products/flexible-video-endoscopes/veezar-video-nasopharyngoscopes' },
    { name: 'Veezar Video Nasopharyngoscope FN20', link: '/products/flexible-video-endoscopes/veezar-video-nasopharyngoscopes' },
    { name: 'Veezar Video Otoscope VO10', link: '/products/compact-cameras/veezar-video-otoscope' },
    { name: 'Veezar Compact Camera CF20', link: '/products/compact-cameras/veezar-compact-camera-cf20' },
    { name: 'Veezar Illumination Head IH10', link: '/products/led-illumination-systems/veezar-illumination-head-ih10' },
    { name: 'Panel PC 21.5" (Tower PC)', link: '/products/documentation-recording-systems/panel-pc-documentation' },
    { name: 'DiVAS 2.8 Software', link: '/products/software/divas-2-8' }
  ],
  applications: [
    'Voice diagnostics with stroboscopy',
    'Otoscopy',
    'Bedside swallowing diagnostics (FEES)',
    'ENT examinations',
    'Phoniatrics'
  ],
  images: [
    '/products/transparent/endocompact-veezar-web.png',
    '/products/transparent/csm-nasopharyngoskop-distale-enden-fn10-fn20-web-43159f20fe.png',
    '/products/transparent/veezar-video-otoskop-web.png',
    '/products/transparent/veezar-kompaktkamera-web.png',
    '/products/transparent/veezar-beleuchtungskopf-ih10-web.png'
  ]
};

export const endoSURGERYSpectarSystem = {
  id: 'endosurgery-spectar-system',
  name: 'EndoSURGERY Spectar',
  slug: 'endosurgery-spectar',
  category: 'Surgery',
  tagline: 'The endoscopy system for all endoscopic applications',
  description: 'The MATRIX P Spectar camera processor in combination with the MATRIX LED duo high-power LED light source forms a solid basis for the imaging provided by the EndoSURGERY system.',
  fullDescription: 'A universal camera processor for highest demands equipped for native sensor resolutions up to 4K/UHD. Together with the DiVAS software, the MATRIX DS data station forms the basis for digitally acquiring, managing and evaluating patient and examination data. The system can be equipped with monitors from 27" to 55", often with a 21.5" monitor with touch function as a second monitor.',
  features: [
    'Consistent Spectar Connector for all 2D and 3D camera heads',
    'Excellent image quality with PIET Image Enhancement',
    'Native sensor resolutions up to 4K/UHD',
    'Improved tissue differentiation',
    'Clear and intuitive control panel',
    'Integration with OR microscopes via C-mount camera head',
    'XION PREMIUM equipment trolley option',
    'Central power control for all devices',
    'Integrated isolation transformer and insulation monitor'
  ],
  piet: [
    { mode: 'PIET lumino', desc: 'Both bright and dark areas are equally well represented.' },
    { mode: 'PIET chromo', desc: 'Details are highlighted and the colour contrast is intensified.' },
    { mode: 'PIET spectro', desc: 'By shifting the colour spectrum, tissue structures are displayed in a more differentiated manner.' }
  ],
  components: [
    { name: 'MATRIX P Spectar Camera Processor', link: '/products/camera-processors-for-surgery/matrix-p-spectar' },
    { name: 'MATRIX LED duo Light Source', link: '/products/led-illumination-systems/matrix-led-duo' },
    { name: 'Spectar 4K Camera Head', link: '/products/camera-heads/spectar-camera-head-4k-uhd' },
    { name: 'Spectar HD Camera Head with Zoom', link: '/products/camera-heads/spectar-camera-head-hd-zoom' },
    { name: 'MATRIX DS Data Station (Tower PC)', link: '/products/documentation-recording-systems/matrix-ds-data-station' },
    { name: 'DiVAS 2.8 Software', link: '/products/software/divas-2-8' },
    { name: 'Medical Grade Monitors 27"-55"', link: '/products/medical-grade-monitors/medical-monitor-27-4k' },
    { name: 'XION PREMIUM Equipment Trolley', link: null }
  ],
  applications: [
    'General surgery',
    'ENT surgery',
    'Urology',
    'Gynecology',
    'Arthroscopy',
    'Minimally invasive surgery'
  ],
  images: [
    '/products/transparent/endosurgery-spectar-web.png',
    '/products/transparent/matrix-p-spectar-web.png',
    '/products/transparent/csm-matrix-led-duo-01-3ac270249d.png',
    '/products/transparent/matrix-ds6-web.png',
    '/products/transparent/spectar-kamerakopf-4k-uhd-web.png'
  ]
};

export const endoSURGERY3DSpectarSystem = {
  id: 'endosurgery-3d-spectar-system',
  name: 'EndoSURGERY 3D Spectar',
  slug: 'endosurgery-3d-spectar',
  category: 'Surgery',
  tagline: 'Premium 3D surgical imaging platform',
  description: 'The EndoSURGERY 3D Spectar system combines the power of MATRIX PL Spectar processor with true 3D visualization for enhanced depth perception in complex surgical procedures.',
  fullDescription: 'Based on the MATRIX PL Spectar processor, this system delivers uncompromising 3D image quality for complex surgical procedures. Supporting both 2D and 3D endoscopy on the same platform, it provides surgeons with enhanced depth perception and spatial awareness for precision surgery.',
  features: [
    'Native 3D imaging with Spectar 3D Camera Head',
    '4K/UHD resolution in both 2D and 3D modes',
    'PIET Image Enhancement for superior tissue differentiation',
    'Seamless switching between 2D and 3D visualization',
    'Integration with 3D medical monitors',
    'OR microscope compatibility',
    'Modular design for future upgrades',
    'DICOM connectivity'
  ],
  piet: [
    { mode: 'PIET lumino', desc: 'Both bright and dark areas are equally well represented.' },
    { mode: 'PIET chromo', desc: 'Details are highlighted and the colour contrast is intensified.' },
    { mode: 'PIET spectro', desc: 'By shifting the colour spectrum, tissue structures are displayed in a more differentiated manner.' }
  ],
  components: [
    { name: 'MATRIX PL Spectar Camera Processor', link: '/products/camera-processors-for-surgery/matrix-pl-spectar' },
    { name: 'Spectar 3D Camera Head', link: '/products/camera-heads/spectar-camera-head-3d-hd' },
    { name: 'Spectar 4K Camera Head', link: '/products/camera-heads/spectar-camera-head-4k-uhd' },
    { name: 'MATRIX LED duo Light Source', link: '/products/led-illumination-systems/matrix-led-duo' },
    { name: '3D Medical Grade Monitors', link: '/products/medical-grade-monitors/medical-monitor-32-surgical' },
    { name: 'MATRIX DS Data Station (Tower PC)', link: '/products/documentation-recording-systems/matrix-ds-data-station' },
    { name: 'DiVAS 2.8 Software', link: '/products/software/divas-2-8' }
  ],
  applications: [
    'Neurosurgery',
    'ENT surgery',
    'Spine surgery',
    'Complex minimally invasive procedures',
    'Microsurgery'
  ],
  images: [
    '/products/transparent/endosurgery-3d-spectar-web.png',
    '/products/transparent/csm-matrix-pl-spectar-web-3bd7424485.png',
    '/products/transparent/spectar-kamerakopf-4k-uhd-web.png',
    '/products/transparent/spectar-kamerakopf-3d-hd-web.png',
    '/products/transparent/spectar-kamerakopf-3d-hd-web.png'
  ]
};

export const endoPORTABLEVeezarSystem = {
  id: 'endoportable-veezar-system',
  name: 'EndoPORTABLE Veezar',
  slug: 'endoportable-veezar',
  category: 'Swallowing Diagnostics',
  tagline: 'Mobile endoscopy system for bedside diagnostics',
  description: 'EndoPORTABLE Veezar is a compact, mobile endoscopy solution ideal for bedside examinations and swallowing diagnostics (FEES).',
  fullDescription: 'The portable Veezar system brings professional endoscopic imaging to the patient bedside. Ideal for FEES (Fiberoptic Endoscopic Evaluation of Swallowing) and mobile ENT examinations, this compact system includes integrated documentation capabilities and battery operation for maximum flexibility.',
  features: [
    'Fully portable and battery-operated',
    'Integrated monitor and documentation',
    'Veezar video nasopharyngoscopes',
    'Compact design for easy transport',
    'Quick setup for bedside use',
    'DiVAS software integration',
    'Perfect for FEES examinations',
    'Lightweight mobile cart'
  ],
  components: [
    { name: 'Veezar Portable Console', link: null },
    { name: 'Veezar Video Nasopharyngoscope', link: '/products/flexible-video-endoscopes/veezar-video-nasopharyngoscopes' },
    { name: 'Integrated Monitor', link: '/products/medical-grade-monitors/medical-monitor-27-4k' },
    { name: 'DiVAS 3.0 Software', link: '/products/software/divas-3-0' },
    { name: 'Battery Power System', link: null },
    { name: 'Mobile Cart', link: null }
  ],
  applications: [
    'Bedside swallowing diagnostics (FEES)',
    'Mobile ENT examinations',
    'ICU consultations',
    'Home care visits',
    'Geriatric assessments'
  ],
  images: [
    '/products/transparent/endoportable-veezar-web.png',
    '/products/transparent/csm-nasopharyngoskop-distale-enden-fn10-fn20-web-43159f20fe.png',
    '/products/transparent/divas-3-0-web.png',
    '/products/transparent/csm-endoportable-veezar-maxado-koffer-web-301da88860.png'
  ]
};


// Flagship complete endoscopy tower — the integrated XION workstation shown in the hero
export const endoscopyTowerSystem = {
  id: 'xion-endoscopy-tower',
  name: 'XION Endoscopy Tower',
  slug: 'endoscopy-tower',
  category: 'Complete integrated workstation',
  tagline: 'One tower. 2D, 3D & 4K. Built around the Spectar platform.',
  description: 'The complete XION endoscopy workstation — a single, harmonised tower integrating the Spectar camera processor, MATRIX LED illumination, DiVAS documentation and medical-grade 4K monitors on one mobile cart.',
  fullDescription: 'The XION Endoscopy Tower brings every component of a modern endoscopy suite together into one engineered, mobile workstation. At its core sits the universal Spectar camera platform, switching freely between 2D, 3D and 4K on a single processor, with hot-swappable camera heads and flexible video endoscopes sharing the same Spectar Connector. Almost-silent MATRIX LED illumination, the MATRIX DS data station running DiVAS software, and medical-grade 4K monitors complete the stack — all cable-managed onto a single cart that rolls from the office to the OR. Engineered and manufactured in Berlin since 1991 and exclusively distributed in the Philippines by FMSInc.',
  features: [
    'Fully integrated tower — processor, illumination, documentation and monitors on one mobile cart',
    'Universal Spectar platform: 2D, 3D and native 4K on a single camera processor',
    'Hot-swappable camera heads & flexible video endoscopes via the universal Spectar Connector',
    'World-first 3D stroboscopy plus noiseless, flicker-free 4K & HD stroboscopy',
    'PIET Professional Image Enhancement Technology — lumino, chromo and spectro modes',
    'Almost-silent MATRIX LED duo illumination for fatigue-free, true-colour visualisation',
    'DiVAS documentation with DICOM / HL7 / GDT connectivity to HIS and practice software',
    'Medical-grade 4K / UHD monitors with patented light-intensity safety control',
    'Configurable from compact office units to full operating-room towers'
  ],
  piet: [
    { mode: 'PIET lumino', desc: 'Both bright and dark areas are equally well represented.' },
    { mode: 'PIET chromo', desc: 'Details are highlighted and the colour contrast is intensified.' },
    { mode: 'PIET spectro', desc: 'By shifting the colour spectrum, tissue structures are displayed in a more differentiated manner.' }
  ],
  divasModules: [
    'Base / Patient Management',
    'Video Documentation (Full HD / 4K, 3D)',
    'Photo Documentation',
    'Stroboscopy Analysis',
    'Swallowing Diagnostics (FEES)',
    'Archiving & PACS / DICOM Connectivity'
  ],
  components: [
    { name: 'Spectar Camera Processor (2D / 3D / 4K)', link: '/products/camera-processors-for-surgery' },
    { name: 'Spectar Camera Head 4K UHD', link: '/products/camera-heads/spectar-camera-head-4k-uhd' },
    { name: 'Spectar Video Nasopharyngoscopes', link: '/products/flexible-video-endoscopes' },
    { name: 'MATRIX LED duo Light Source', link: '/products/led-illumination-systems' },
    { name: 'MATRIX DS Data Station (Tower PC)', link: '/products/documentation-recording-systems' },
    { name: 'DiVAS Documentation Software', link: '/products/software/divas-2-8' },
    { name: 'Medical-grade 4K Monitors', link: '/products/medical-grade-monitors' }
  ],
  applications: [
    'ENT video endoscopy (office · ambulance · OR)',
    'Laryngo-stroboscopy & phoniatrics (2D, HD, 4K, 3D)',
    'Endoscopic surgery & microscopy',
    'Swallowing diagnostics (FEES)',
    'Documentation, archiving & teaching'
  ],
  images: [
    '/assets/hero-tower-static.png',
    '/products/transparent/spectar-kamerakopf-4k-uhd-web.png',
    '/products/transparent/csm-matrix-led-duo-01-3ac270249d.png',
    '/products/transparent/xion-monitore-web.png',
    '/products/transparent/matrix-ds6-web.png',
    '/products/transparent/spectar-video-nasopharyngoskop-sterrad-web.png'
  ]
};
